import Config from 'src/common/Config';
import {
  SettingsFlow,
  RecoveryFlow,
  LoginFlow,
  RegistrationFlow,
  UiNode,
  UiNodeInputAttributes,
} from '@ory/client';
import { filterNodesByGroups, isUiNodeInputAttributes } from '@ory/integrations/ui';
import { AuthMethod } from 'src/auth/settings/types';

interface OryApiError {
  response?: {
    data?: unknown;
    status?: number;
  };
}

export const isOryApiError = (err: unknown): err is OryApiError =>
  typeof err === 'object' && err !== null && 'response' in err;

export const getOryErrorDetails = (
  err: unknown,
): { status?: number; oryErrorId?: unknown; oryErrorReason?: unknown } => {
  if (!isOryApiError(err)) return {};

  const status = err.response?.status;
  const data = err.response?.data;
  if (!data || typeof data !== 'object' || !('error' in data)) return { status };

  const { error } = data;
  if (!error || typeof error !== 'object') return { status };

  return {
    status,
    oryErrorId: 'id' in error ? error.id : undefined,
    oryErrorReason: 'reason' in error ? error.reason : undefined,
  };
};

export const rewriteActionUrl = (actionUrl: string): string => {
  if (!actionUrl) return '';
  try {
    const url = new URL(actionUrl);
    const tunnelBase = Config.getAuthBasePath();
    return `${tunnelBase}${url.pathname}${url.search}`;
  } catch {
    return actionUrl;
  }
};

export const getCsrfTokenFromFlow = (
  flow: SettingsFlow | RecoveryFlow | LoginFlow | RegistrationFlow | undefined,
): string => {
  if (!flow) return '';

  const csrfNode = flow.ui.nodes.find(
    node =>
      isUiNodeInputAttributes(node.attributes) &&
      (node.attributes as UiNodeInputAttributes).name === 'csrf_token',
  );

  return (csrfNode?.attributes as UiNodeInputAttributes | undefined)?.value ?? '';
};

export const getOidcNodesByAction = (
  flow: SettingsFlow | undefined,
  actions: string[],
): UiNode[] => {
  if (!flow) return [];

  const oidcGroupNodes = filterNodesByGroups({ nodes: flow.ui.nodes, groups: ['oidc'] });

  return oidcGroupNodes.filter(
    node =>
      isUiNodeInputAttributes(node.attributes) &&
      actions.includes((node.attributes as UiNodeInputAttributes).name),
  );
};

type SsoProvider = 'microsoft' | 'google';

const SSO_PROVIDER_MAP: Record<Exclude<AuthMethod, 'password'>, SsoProvider> = {
  sso_only_microsoft: 'microsoft',
  sso_only_google: 'google',
};

export const isSsoOnly = (authMethod: AuthMethod | undefined): boolean =>
  authMethod !== undefined && authMethod !== 'password';

const getSsoProvider = (authMethod: AuthMethod): SsoProvider | undefined => {
  if (authMethod === 'password') return undefined;
  return SSO_PROVIDER_MAP[authMethod];
};

export const filterOidcNodesByAuthMethod = (nodes: UiNode[], authMethod: AuthMethod): UiNode[] => {
  const provider = getSsoProvider(authMethod);
  if (!provider) return nodes;

  return nodes.filter(node => {
    if (!isUiNodeInputAttributes(node.attributes)) return false;
    const value = (node.attributes as UiNodeInputAttributes).value as string;
    return value?.includes(provider);
  });
};
